/* Register — Trade Republic style multi-step registration wizard.
 *
 * FLOW:
 *   1. Welcome        — "Cosa faremo insieme": intro + benefits
 *   2. Nome + Email   — asks the human
 *   3. Password       — password only (better UX than mixing with email)
 *   4. Nome cane      — the emotional hook, one-field screen
 *   5. Features intro — a mini tour of ORIO (Cassetto / SOS / Match)
 *   6. Consensi       — GDPR: terms, geo, notifications (mandatory)
 *   7. Trial offer    — big card, activate 30 giorni Pro (no card)
 *   8. Done           — confetti / success handoff to /app
 *
 * DESIGN RULES:
 *   • One primary action per screen (Trade Republic style)
 *   • Large ORIO illustration OR a big visual element at the top
 *   • Warm cream/peach background, orio-grad accents on key words
 *   • Header: back arrow + progress dots + logo
 *   • Framer Motion crossfade between steps
 *
 * BUSINESS RULES (ORIO):
 *   • Only 2 plans: FREE and PRO. Trial is treated as PRO (backend
 *     already derives `is_pro = in_trial or is_subscribed`).
 *   • Card is NEVER required at signup. It becomes optional (Profile →
 *     Abbonamento) or mandatory only after trial expires.
 */
import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, ArrowRight, Check, Sparkles, PawPrint, Shield, MapPin,
  Bell, HeartHandshake, Briefcase, AlertTriangle, Loader2, Crown,
  Mail, User as UserIcon, Lock, Eye, EyeOff, Heart,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatErr } from "@/lib/api";
import { LOGO_WHITE } from "@/lib/assets";
import { orioIllustration, orioIllustrationFallback } from "@/lib/illustrations";
import { hapticTap, hapticSuccess } from "@/lib/haptics";

const STEPS = ["welcome", "email", "password", "dog", "features", "consent", "trial", "done"];

/* Shared frame — cream bg, top header w/ progress + back, painterly ORIO glow. */
function StepFrame({ step, total, onBack, showBack = true, children }) {
  return (
    <div className="min-h-[100dvh] relative overflow-hidden"
         style={{ background: "#FBF7F1", color: "#1A1A1A" }}>
      {/* Ambient warm glow */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full"
             style={{ background: "radial-gradient(circle, rgba(255,122,26,0.16) 0%, transparent 60%)" }} />
        <div className="absolute -bottom-40 -left-40 w-[600px] h-[600px] rounded-full"
             style={{ background: "radial-gradient(circle, rgba(45,186,96,0.12) 0%, transparent 60%)" }} />
      </div>
      {/* Header — back + progress + logo */}
      <div className="relative z-10 pt-safe px-5 pt-5 flex items-center justify-between">
        {showBack ? (
          <button onClick={onBack} data-testid="reg-back"
                  className="h-9 w-9 rounded-full bg-[#F4EFE6] hover:bg-[#EAE3D2] flex items-center justify-center">
            <ArrowLeft className="h-4 w-4 text-[#5A5A5A]" />
          </button>
        ) : <div className="w-9" />}
        {/* Progress dots */}
        <div className="flex items-center gap-1.5" aria-label={`Passo ${step + 1} di ${total}`}>
          {Array.from({ length: total }).map((_, i) => (
            <span key={i} className="transition-all"
              style={{
                height: 6,
                width: i === step ? 22 : 6,
                borderRadius: 6,
                background: i <= step
                  ? "linear-gradient(90deg, #FF7A1A 0%, #FFB020 100%)"
                  : "#EAE3D2",
              }} />
          ))}
        </div>
        <Link to="/" aria-label="ORIO" className="h-6 w-[62px]"
              data-testid="reg-logo"
              style={{
                background: "linear-gradient(120deg, #FF7A1A 0%, #F4B83C 50%, #2DBA60 100%)",
                backgroundSize: "200% 200%",
                animation: "orio-bg-shift 6s ease infinite",
                WebkitMask: `url(${LOGO_WHITE}) no-repeat center / contain`,
                mask: `url(${LOGO_WHITE}) no-repeat center / contain`,
                display: "block",
              }} />
      </div>
      {/* Content area */}
      <div className="relative z-10 max-w-md mx-auto px-5 pt-4 pb-24">
        {children}
      </div>
    </div>
  );
}

/* Big hero illustration slot — TR-style tall PORTRAIT card (3:4 aspect).
   Vertical framing ensures the ORIO v23 hybrid images (subjects + pastel
   painted background) render fully without cropping.
   Uses an <img> element with onError swap so the Unsplash fallback appears
   only when the ORIO asset actually fails — never flashes during load. */
function HeroIllustration({ slug }) {
  const [errored, setErrored] = React.useState(false);
  const src = errored ? orioIllustrationFallback(slug) : orioIllustration(slug);
  return (
    <div className="rounded-[28px] overflow-hidden border relative w-full"
         style={{
           aspectRatio: "3 / 4",
           borderColor: "#F0EBE3",
           boxShadow: "0 22px 44px -18px rgba(0,0,0,0.12)",
           background: "#FFF5E9",
         }}>
      <img
        src={src}
        alt=""
        loading="eager"
        decoding="async"
        onError={() => setErrored(true)}
        className="absolute inset-0 w-full h-full object-cover"
        style={{ background: "#FFF5E9" }}
      />
    </div>
  );
}

/* Small thumbnail variant used inside the feature list card of Step 4.
   Same behavior as HeroIllustration: <img> with onError fallback swap
   so the Unsplash fallback never flashes during load. */
function FeatThumb({ slug }) {
  const [errored, setErrored] = React.useState(false);
  const src = errored ? orioIllustrationFallback(slug) : orioIllustration(slug);
  return (
    <img
      src={src}
      alt=""
      loading="lazy"
      decoding="async"
      onError={() => setErrored(true)}
      className="absolute inset-0 w-full h-full object-cover"
    />
  );
}

/* Primary CTA — orange gradient, tall pill. */
function PrimaryButton({ children, onClick, disabled, testid, icon: Icon = ArrowRight }) {
  return (
    <motion.button
      whileTap={{ scale: 0.97 }}
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full text-white text-[15px] font-semibold disabled:opacity-60"
      style={{
        background: "linear-gradient(135deg, #FF7A1A 0%, #E94545 100%)",
        boxShadow: "0 20px 40px -14px rgba(233,69,69,0.55)",
      }}
    >
      {disabled ? <Loader2 className="h-5 w-5 animate-spin" /> : <>
        {children}
        {Icon && <Icon className="h-4 w-4" />}
      </>}
    </motion.button>
  );
}

/* ---------- Individual steps ---------- */

function StepWelcome({ next }) {
  return (
    <>
      <HeroIllustration slug="reg-welcome" />
      <div className="mt-6">
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#C25612] inline-flex items-center gap-1.5 font-semibold">
          <Sparkles className="h-3 w-3" /> Benvenuto in ORIO
        </div>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl leading-[1.05] tracking-tight">
          La rete<br />che protegge<br /><span className="orio-grad-text">il tuo cane.</span>
        </h1>
        <p className="mt-3 text-[14px] text-[#6B6B6B] leading-relaxed">
          Ci vuole un minuto. Ti facciamo qualche domanda per personalizzare
          la tua esperienza, poi sei dentro.
        </p>
        <ul className="mt-5 space-y-2.5" data-testid="reg-welcome-bullets">
          {[
            { t: "Passaporto digitale del tuo cane", i: PawPrint },
            { t: "SOS geolocalizzato · community 80 km", i: Shield },
            { t: "Passeggiate tracciate come Apple Fitness", i: MapPin },
          ].map((b, i) => (
            <li key={i} className="flex items-center gap-3 text-[13.5px] text-[#1A1A1A]">
              <span className="h-8 w-8 rounded-xl flex items-center justify-center"
                    style={{ background: "#FFEDD5", color: "#C25612" }}>
                <b.i className="h-4 w-4" />
              </span>
              {b.t}
            </li>
          ))}
        </ul>
      </div>
      <div className="mt-7">
        <PrimaryButton onClick={next} testid="reg-welcome-next">Iniziamo</PrimaryButton>
        <p className="mt-3 text-center text-[12px] text-[#7A7A7A]">
          Hai già un account? <Link to="/login" className="text-[#1A1A1A] underline font-semibold">Accedi</Link>
        </p>
      </div>
    </>
  );
}

function StepEmail({ name, setName, email, setEmail, next }) {
  const valid = name.trim().length >= 2 && /\S+@\S+\.\S+/.test(email);
  return (
    <>
      <HeroIllustration slug="reg-hello" />
      <div className="mt-6">
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#C25612] inline-flex items-center gap-1.5 font-semibold">
          <UserIcon className="h-3 w-3" /> Passo 1 · Il tuo profilo
        </div>
        <h1 className="mt-2 font-display text-4xl leading-[1.05] tracking-tight">
          Come ti <span className="orio-grad-text">chiami</span>?
        </h1>
        <p className="mt-2 text-[13.5px] text-[#6B6B6B] leading-relaxed">
          Nome e email li usiamo per proteggere il tuo account e per avvisarti
          in caso di emergenza sul tuo cane.
        </p>

        <label className="block mt-5">
          <span className="text-[10px] uppercase tracking-[0.20em] text-[#7A7A7A] font-semibold">Nome</span>
          <input
            data-testid="reg-name" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Marco" autoFocus
            className="mt-1.5 w-full px-4 py-3.5 rounded-2xl border bg-white text-[15px]"
            style={{ borderColor: "#F0EBE3" }} />
        </label>

        <label className="block mt-3">
          <span className="text-[10px] uppercase tracking-[0.20em] text-[#7A7A7A] font-semibold">Email</span>
          <input
            data-testid="reg-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="tu@esempio.it" autoComplete="email"
            className="mt-1.5 w-full px-4 py-3.5 rounded-2xl border bg-white text-[15px]"
            style={{ borderColor: "#F0EBE3" }} />
        </label>
      </div>
      <div className="mt-7">
        <PrimaryButton onClick={next} disabled={!valid} testid="reg-email-next">Continua</PrimaryButton>
      </div>
    </>
  );
}

function StepPassword({ password, setPassword, next }) {
  const [show, setShow] = useState(false);
  const valid = password.length >= 6;
  return (
    <>
      <HeroIllustration slug="reg-security" />
      <div className="mt-6">
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#C25612] inline-flex items-center gap-1.5 font-semibold">
          <Lock className="h-3 w-3" /> Passo 2 · Sicurezza
        </div>
        <h1 className="mt-2 font-display text-4xl leading-[1.05] tracking-tight">
          Scegli una <span className="orio-grad-text">password.</span>
        </h1>
        <p className="mt-2 text-[13.5px] text-[#6B6B6B] leading-relaxed">
          Almeno 6 caratteri. Consigliamo mix di lettere, numeri e un simbolo per
          proteggere le informazioni del tuo cane.
        </p>

        <label className="block mt-5 relative">
          <span className="text-[10px] uppercase tracking-[0.20em] text-[#7A7A7A] font-semibold">Password</span>
          <input
            data-testid="reg-password" type={show ? "text" : "password"} value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus autoComplete="new-password" placeholder="••••••"
            className="mt-1.5 w-full px-4 py-3.5 pr-12 rounded-2xl border bg-white text-[15px] tracking-widest"
            style={{ borderColor: "#F0EBE3" }} />
          <button type="button" onClick={() => setShow((s) => !s)}
                  className="absolute right-3 top-[calc(50%+6px)] -translate-y-1/2 h-9 w-9 rounded-full flex items-center justify-center text-[#7A7A7A] hover:bg-[#F4EFE6]"
                  data-testid="reg-password-toggle" aria-label="Mostra password">
            {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </label>

        <div className="mt-2 text-[11.5px] text-[#7A7A7A]">
          {valid
            ? <span className="text-[#2DBA60]">✓ Password valida</span>
            : "Minimo 6 caratteri"}
        </div>
      </div>
      <div className="mt-7">
        <PrimaryButton onClick={next} disabled={!valid} testid="reg-password-next">Continua</PrimaryButton>
      </div>
    </>
  );
}

function StepDog({ dogName, setDogName, next }) {
  return (
    <>
      <HeroIllustration slug="reg-dog-name" />
      <div className="mt-6">
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#C25612] inline-flex items-center gap-1.5 font-semibold">
          <PawPrint className="h-3 w-3" /> Passo 3 · Il tuo compagno
        </div>
        <h1 className="mt-2 font-display text-4xl leading-[1.05] tracking-tight">
          Come si chiama<br /><span className="orio-grad-text">il tuo cane</span>?
        </h1>
        <p className="mt-2 text-[13.5px] text-[#6B6B6B] leading-relaxed">
          Ci basta il nome per iniziare. Il passaporto digitale completo lo
          crei dopo con foto, microchip, razza e visite veterinarie.
        </p>

        <label className="block mt-5">
          <span className="text-[10px] uppercase tracking-[0.20em] text-[#7A7A7A] font-semibold">Nome del cane</span>
          <input
            data-testid="reg-dog-name" value={dogName} onChange={(e) => setDogName(e.target.value)}
            placeholder="Buddy · Luna · Rocky…" autoFocus
            className="mt-1.5 w-full px-4 py-3.5 rounded-2xl border bg-white text-[15px]"
            style={{ borderColor: "#F0EBE3" }} />
        </label>

        <div className="mt-4 rounded-2xl p-4 border" style={{ background: "#FFF6E6", borderColor: "#FFE0B8" }}>
          <div className="flex items-start gap-2.5">
            <Sparkles className="h-4 w-4 text-[#C25612] shrink-0 mt-0.5" />
            <div className="text-[12.5px] text-[#5A4A2E] leading-snug">
              Se non hai ancora un cane oppure preferisci configurare dopo, puoi
              saltare — potrai aggiungerlo in qualsiasi momento dal profilo.
            </div>
          </div>
        </div>
      </div>
      <div className="mt-6 space-y-2">
        <PrimaryButton onClick={next} testid="reg-dog-next">
          {dogName.trim() ? `Continua con ${dogName.trim()}` : "Continua"}
        </PrimaryButton>
        {!dogName.trim() && (
          <button onClick={next} data-testid="reg-dog-skip"
                  className="w-full py-2.5 text-[12.5px] text-[#7A7A7A] hover:text-[#1A1A1A]">
            Salta per ora
          </button>
        )}
      </div>
    </>
  );
}

function StepFeatures({ next }) {
  const feats = [
    { title: "Passaporto digitale", body: "Microchip, vaccini e visite sempre a portata di mano.",
      icon: PawPrint, color: "#FF7A1A", slug: "tip-passaporto-v2" },
    { title: "SOS geolocalizzato", body: "Allerti fino a 80 km. La community risponde.",
      icon: AlertTriangle, color: "#E94545", slug: "tip-sos" },
    { title: "Passeggiate tracciate", body: "GPS, km, calorie e streak Karma. Come Apple Fitness.",
      icon: HeartHandshake, color: "#2DBA60", slug: "feat-walk" },
  ];
  return (
    <>
      <div>
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#C25612] inline-flex items-center gap-1.5 font-semibold">
          <Heart className="h-3 w-3" /> Passo 4 · Cosa fa ORIO
        </div>
        <h1 className="mt-2 font-display text-4xl leading-[1.05] tracking-tight">
          Le cose che <span className="orio-grad-text">userai ogni giorno.</span>
        </h1>
        <p className="mt-2 text-[13.5px] text-[#6B6B6B] leading-relaxed">
          Tre motivi per cui i padroni scelgono ORIO.
        </p>

        <div className="mt-5 space-y-3">
          {feats.map((f, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * i, type: "spring", damping: 22 }}
              className="rounded-2xl border overflow-hidden flex items-stretch"
              style={{ background: "white", borderColor: "#F0EBE3",
                       boxShadow: "0 8px 22px -12px rgba(0,0,0,0.10)" }}
              data-testid={`reg-feat-${i}`}
            >
              <div className="w-[112px] shrink-0 relative"
                   style={{ background: `${f.color}12` }}>
                <FeatThumb slug={f.slug} />
              </div>
              <div className="flex-1 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className="h-6 w-6 rounded-lg flex items-center justify-center"
                        style={{ background: `${f.color}18`, color: f.color }}>
                    <f.icon className="h-3.5 w-3.5" />
                  </span>
                  <span className="text-[9.5px] uppercase tracking-[0.14em] font-bold" style={{ color: f.color }}>
                    Novità
                  </span>
                </div>
                <div className="font-display font-bold text-[16px] text-[#0F0F0F] leading-tight">{f.title}</div>
                <div className="text-[12px] text-[#6B6B6B] mt-1 leading-snug">{f.body}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
      <div className="mt-6">
        <PrimaryButton onClick={next} testid="reg-features-next">Continua</PrimaryButton>
      </div>
    </>
  );
}

function ConsentRow({ id, checked, onChange, testid, title, body, required }) {
  return (
    <label htmlFor={id} className="flex items-start gap-3 py-3.5 border-b cursor-pointer"
           style={{ borderColor: "#F0EBE3" }}>
      <input id={id} data-testid={testid} type="checkbox"
             checked={checked} onChange={(e) => onChange(e.target.checked)}
             className="mt-0.5 h-5 w-5 shrink-0 accent-[#FF7A1A]" />
      <div className="flex-1">
        <div className="text-[13.5px] font-semibold text-[#1A1A1A]">
          {title} {required && <span className="text-[10px] text-[#E94545]">obbligatorio</span>}
        </div>
        <div className="text-[11.5px] text-[#6B6B6B] leading-snug mt-0.5">{body}</div>
      </div>
    </label>
  );
}

function StepConsent({ termsAccepted, setTermsAccepted, geoAccepted, setGeoAccepted,
                      notifAccepted, setNotifAccepted, marketing, setMarketing, next }) {
  const allMandatory = termsAccepted && geoAccepted && notifAccepted;
  return (
    <>
      <div>
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#C25612] inline-flex items-center gap-1.5 font-semibold">
          <Shield className="h-3 w-3" /> Passo 5 · Consensi
        </div>
        <h1 className="mt-2 font-display text-4xl leading-[1.05] tracking-tight">
          Un attimo di <span className="orio-grad-text">privacy.</span>
        </h1>
        <p className="mt-2 text-[13.5px] text-[#6B6B6B] leading-relaxed">
          ORIO ha bisogno di alcuni permessi essenziali per funzionare in
          sicurezza. Puoi modificarli in qualsiasi momento dal profilo.
        </p>

        <div className="mt-4">
          <ConsentRow id="c-terms" checked={termsAccepted} onChange={setTermsAccepted} required
               testid="reg-consent-terms" title="Accetto Termini e Privacy"
               body="Leggi i Termini e l'Informativa Privacy dai link in fondo." />
          <ConsentRow id="c-geo" checked={geoAccepted} onChange={setGeoAccepted} required
               testid="reg-consent-geo" title="Uso della geolocalizzazione"
               body="Per SOS, veterinari e pericoli nella tua zona. Non condividiamo la tua posizione." />
          <ConsentRow id="c-notif" checked={notifAccepted} onChange={setNotifAccepted} required
               testid="reg-consent-notif" title="Notifiche critiche di sicurezza"
               body="Solo emergenze reali (SOS locali). Zero spam." />
          <ConsentRow id="c-marketing" checked={marketing} onChange={setMarketing}
               testid="reg-consent-marketing" title="Aggiornamenti prodotto (facoltativo)"
               body="Ricevi tips e novità ORIO via email. Disattivabile in ogni momento." />
        </div>

        <div className="mt-3 text-[11px] text-[#7A7A7A]">
          Vedi <a href="/terms" className="text-[#1A1A1A] underline">Termini</a> e{" "}
          <a href="/privacy" className="text-[#1A1A1A] underline">Privacy</a>.
        </div>
      </div>
      <div className="mt-6">
        <PrimaryButton onClick={next} disabled={!allMandatory} testid="reg-consent-next">
          Continua
        </PrimaryButton>
      </div>
    </>
  );
}

function StepTrial({ activateAndFinish, skipTrial, submitting }) {
  return (
    <>
      <div>
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#C25612] inline-flex items-center gap-1.5 font-semibold">
          <Crown className="h-3 w-3" /> Passo 6 · ORIO Pro
        </div>
        <h1 className="mt-2 font-display text-4xl leading-[1.05] tracking-tight">
          30 giorni <span className="orio-grad-text">gratuiti.</span><br />Senza carta.
        </h1>
        <p className="mt-2 text-[13.5px] text-[#6B6B6B] leading-relaxed">
          Provi tutto — SOS, Match, Cassetto, ORIO Walk — per 30 giorni.
          Se non aggiungi una carta prima della scadenza, torni gratis.
          Zero addebiti a sorpresa.
        </p>

        <motion.div
          initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, type: "spring", damping: 22 }}
          className="mt-5 rounded-[28px] p-5 border relative overflow-hidden"
          style={{
            background: "linear-gradient(135deg, #FFF6E6 0%, #FFEDD5 50%, #FFE0F0 100%)",
            borderColor: "rgba(255,122,26,0.30)",
            boxShadow: "0 20px 40px -14px rgba(255,122,26,0.45)",
          }}
        >
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-2xl flex items-center justify-center text-white shrink-0"
                 style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #FFB020 100%)" }}>
              <Crown className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.20em] font-bold text-[#C25612]">
                ORIO Pro · 30 giorni
              </div>
              <div className="font-display font-bold text-[24px] text-[#1A1A1A] leading-none mt-1">
                Gratis
              </div>
            </div>
          </div>
          <ul className="mt-4 space-y-1.5">
            {[
              "Pulsante SOS · allerta 80 km",
              "Match passeggiate compatibili",
              "Missioni: crea e partecipa",
              "Cassetto digitale con promemoria",
              "ORIO Walk · GPS + karma streak",
              "Cani illimitati sul profilo",
            ].map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-[12.5px] text-[#1A1A1A]">
                <span className="h-4 w-4 rounded-full bg-[#34D39918] flex items-center justify-center shrink-0 mt-0.5">
                  <Check className="h-2.5 w-2.5 text-[#2DBA60]" />
                </span>
                {b}
              </li>
            ))}
          </ul>
          <div className="mt-4 text-[11px] text-[#5A4A2E] leading-snug">
            Dopo 30 giorni: <strong>4,99 €/mese</strong>. Aggiungi la carta quando vuoi da{" "}
            <strong>Profilo → Abbonamento</strong>. Se non lo fai, torni gratis in automatico.
          </div>
        </motion.div>
      </div>
      <div className="mt-6 space-y-2">
        <PrimaryButton onClick={activateAndFinish} disabled={submitting}
                       testid="reg-trial-activate" icon={Sparkles}>
          Attiva 30 giorni Pro
        </PrimaryButton>
        <button onClick={skipTrial} disabled={submitting} data-testid="reg-trial-skip"
                className="w-full py-3 text-[12.5px] text-[#7A7A7A] hover:text-[#1A1A1A]">
          Continua col piano Free
        </button>
      </div>
    </>
  );
}

function StepDone({ dogName, onEnter }) {
  useEffect(() => { hapticSuccess(); }, []);
  return (
    <>
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", damping: 18 }}
        className="mt-6 mx-auto h-24 w-24 rounded-full flex items-center justify-center relative"
        style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #FFB020 60%, #34D399 100%)",
                 boxShadow: "0 26px 60px -14px rgba(255,122,26,0.55)" }}
      >
        {[0, 1, 2].map((i) => (
          <motion.div key={i} aria-hidden
            initial={{ scale: 0.9, opacity: 0.55 }}
            animate={{ scale: [0.9, 1.9], opacity: [0.55, 0] }}
            transition={{ duration: 2, delay: i * 0.5, repeat: Infinity, ease: "easeOut" }}
            className="absolute inset-0 rounded-full"
            style={{ border: "1.5px solid rgba(255,176,32,0.6)" }} />
        ))}
        <Check className="h-10 w-10 text-white relative z-10" strokeWidth={2.5} />
      </motion.div>
      <div className="mt-8 text-center">
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#C25612] inline-flex items-center gap-1.5 font-semibold">
          <Sparkles className="h-3 w-3" /> Sei dentro
        </div>
        <h1 className="mt-2 font-display text-4xl leading-[1.05] tracking-tight">
          Benvenuto in <span className="orio-grad-text">ORIO</span>
          {dogName ? `,` : "."}
          {dogName && <> con <span className="orio-grad-text">{dogName}</span>.</>}
        </h1>
        <p className="mt-3 text-[13.5px] text-[#6B6B6B] leading-relaxed">
          Il tuo account è pronto. Da qui puoi creare il passaporto digitale
          e iniziare a proteggere il tuo cane con la community ORIO.
        </p>
      </div>
      <div className="mt-8">
        <PrimaryButton onClick={onEnter} testid="reg-done-enter" icon={ArrowRight}>
          Entra in ORIO
        </PrimaryButton>
      </div>
    </>
  );
}

/* ---------- Wizard Controller ---------- */

export default function Register() {
  const { register, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = location.state || JSON.parse(sessionStorage.getItem("orio_prefill") || "{}");
  const refCode = new URLSearchParams(location.search).get("ref") || "";

  const [step, setStep] = useState(0);
  const [name, setName] = useState(prefill.name || "");
  const [email, setEmail] = useState(prefill.email || "");
  const [password, setPassword] = useState("");
  const [dogName, setDogName] = useState(prefill.dog_name || "");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [geoAccepted, setGeoAccepted] = useState(false);
  const [notifAccepted, setNotifAccepted] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [registered, setRegistered] = useState(false);

  useEffect(() => { sessionStorage.removeItem("orio_prefill"); }, []);

  const goBack = () => {
    if (step === 0) navigate("/");
    else setStep((s) => Math.max(0, s - 1));
  };
  const goNext = () => { hapticTap(); setStep((s) => Math.min(STEPS.length - 1, s + 1)); };

  // Register the user first (called from the trial step), then optionally
  // activate the in-DB trial before showing the Done step.
  const doRegister = async () => {
    try {
      await register({
        email, password, name,
        dog_name: dogName || undefined,
        ref: refCode || undefined,
        terms_accepted: termsAccepted,
        geo_accepted: geoAccepted,
        critical_notifications_accepted: notifAccepted,
        marketing_opt_in: marketing,
      });
      setRegistered(true);
      return true;
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message || "Errore registrazione");
      return false;
    }
  };

  const activateAndFinish = async () => {
    setSubmitting(true);
    try {
      const ok = registered || await doRegister();
      if (!ok) return;
      // Activate the 30-day trial in DB (no card).
      try {
        await api.post("/subscription/start-trial");
        try { await refresh?.(); } catch { /* refresh error is non-fatal */ }
        toast.success("30 giorni Pro attivati!");
      } catch (e) {
        // Not fatal — account exists, they can activate later from Profilo.
        toast.error("Non sono riuscito ad attivare la prova ora. Potrai farlo dal profilo.");
      }
      setStep(STEPS.indexOf("done"));
    } finally { setSubmitting(false); }
  };

  const skipTrial = async () => {
    setSubmitting(true);
    try {
      const ok = registered || await doRegister();
      if (!ok) return;
      try { await refresh?.(); } catch { /* refresh error is non-fatal */ }
      setStep(STEPS.indexOf("done"));
    } finally { setSubmitting(false); }
  };

  const total = STEPS.length;
  const current = STEPS[step];

  return (
    <StepFrame step={step} total={total} onBack={goBack} showBack={step > 0 && current !== "done"}>
      <AnimatePresence mode="wait">
        <motion.div
          key={current}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.28 }}
        >
          {current === "welcome" && <StepWelcome next={goNext} />}
          {current === "email" && <StepEmail name={name} setName={setName} email={email} setEmail={setEmail} next={goNext} />}
          {current === "password" && <StepPassword password={password} setPassword={setPassword} next={goNext} />}
          {current === "dog" && <StepDog dogName={dogName} setDogName={setDogName} next={goNext} />}
          {current === "features" && <StepFeatures next={goNext} />}
          {current === "consent" && (
            <StepConsent
              termsAccepted={termsAccepted} setTermsAccepted={setTermsAccepted}
              geoAccepted={geoAccepted} setGeoAccepted={setGeoAccepted}
              notifAccepted={notifAccepted} setNotifAccepted={setNotifAccepted}
              marketing={marketing} setMarketing={setMarketing}
              next={goNext}
            />
          )}
          {current === "trial" && (
            <StepTrial activateAndFinish={activateAndFinish} skipTrial={skipTrial} submitting={submitting} />
          )}
          {current === "done" && (
            <StepDone dogName={dogName}
              onEnter={() => {
                const target = dogName
                  ? `/app/cani/nuovo?welcome=1&dog=${encodeURIComponent(dogName)}`
                  : "/app?welcome=1";
                navigate(target, { replace: true });
              }} />
          )}
        </motion.div>
      </AnimatePresence>
    </StepFrame>
  );
}
